import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./PersonalLoan.css";
import "animate.css/animate.min.css";
import Seo from "../components/Seo";

const loanUses = [
  {
    icon: "bi bi-heart",
    title: "Wedding Expenses",
    description:
      "Manage eligible venue, catering, decoration, clothing and other wedding-related expenses.",
  },
  {
    icon: "bi bi-house-gear",
    title: "Home Renovation",
    description:
      "Finance eligible repair, furnishing, remodelling and home-improvement requirements.",
  },
  {
    icon: "bi bi-airplane",
    title: "Travel and Vacation",
    description:
      "Plan eligible domestic or international travel expenses without immediately using your entire savings.",
  },
  {
    icon: "bi bi-hospital",
    title: "Medical Requirements",
    description:
      "Arrange funds for eligible hospitalisation, treatment, medicine and healthcare expenses.",
  },
  {
    icon: "bi bi-mortarboard",
    title: "Education and Upskilling",
    description:
      "Support eligible tuition, certification, training and professional-development expenses.",
  },
  {
    icon: "bi bi-arrow-repeat",
    title: "Debt Consolidation",
    description:
      "Consolidate eligible high-cost obligations into a structured repayment plan, subject to lender approval.",
  },
];

const eligibilityItems = [
  {
    image: "/images/Bank Pages/salaried-image-icon.png",
    alt: "Personal-loan applicant profile",
    title: "Applicant Profile",
    description:
      "Salaried, self-employed and eligible professional applicants may apply.",
  },
  {
    image: "/images/Bank Pages/age.png",
    alt: "Personal-loan applicant age",
    title: "Applicant Age",
    description:
      "Age criteria depend on the selected lender, employment profile and requested tenure.",
  },
  {
    image: "/images/Bank Pages/3-month-experienced.png",
    alt: "Employment stability for personal loan",
    title: "Employment Stability",
    description:
      "Stable employment, professional income or established business activity may be required.",
  },
  {
    image: "/images/Bank Pages/minimum-income_20000.png",
    alt: "Personal-loan income eligibility",
    title: "Monthly Income",
    description:
      "Minimum income requirements vary by lender, city, employer and applicant profile.",
  },
];

const documentGroups = [
  {
    title: "Identity Documents",
    image: "/images/Bank Pages/photo-identy.png",
    items: [
      "PAN Card",
      "Aadhaar Card",
      "Passport",
      "Voter ID",
      "Driving Licence",
      "Recent passport-size photographs",
    ],
  },
  {
    title: "Address Documents",
    image: "/images/Bank Pages/proof-of-residence.png",
    items: [
      "Aadhaar Card",
      "Passport",
      "Driving Licence",
      "Registered rent agreement",
      "Recent utility bill",
      "Bank statement showing current address",
    ],
  },
  {
    title: "Income Documents",
    image: "/images/Bank Pages/income-proof.png",
    items: [
      "Recent salary slips",
      "Salary account bank statements",
      "Form 16",
      "Income tax returns",
      "Audited financial statements for self-employed applicants",
      "Business registration proof, where applicable",
    ],
  },
  {
    title: "Employment and Supporting Documents",
    image: "/images/Bank Pages/other-document.png",
    items: [
      "Employee ID card",
      "Employment confirmation letter",
      "Existing loan statements",
      "Current sanction letters",
      "Business registration documents",
      "Additional lender-specific documents",
    ],
  },
];

const applicationSteps = [
  {
    title: "Share Your Requirement",
    description:
      "Tell us the required loan amount, intended purpose, employment profile and preferred tenure.",
  },
  {
    title: "Initial Eligibility Review",
    description:
      "Our team reviews income, employment stability, existing obligations and basic credit eligibility.",
  },
  {
    title: "Compare Lender Options",
    description:
      "Compare suitable lender options based on indicative rates, tenure, charges and profile requirements.",
  },
  {
    title: "Submit Documents",
    description:
      "Submit KYC, banking, employment and income documents with our assistance.",
  },
  {
    title: "Lender Assessment",
    description:
      "The lender verifies your documents, credit history, repayment capacity and employment profile.",
  },
  {
    title: "Approval and Disbursement",
    description:
      "After approval and completion of formalities, the sanctioned amount is disbursed.",
  },
];

const lenderRates = [
  {
    lender: "IDFC FIRST Bank",
    link:
      "https://www.idfcfirstbank.com/personal-banking/loans/personal-loan",
    rate: "9.99%*",
    amount: "Up to ₹50 lakh*",
    tenure: "Up to 72 months",
    fee: "As per bank policy",
  },
  {
    lender: "Bajaj Finance",
    link: "https://www.bajajfinserv.in/personal-loan",
    rate: "10.00%*",
    amount: "Up to ₹50 lakh*",
    tenure: "Up to 96 months",
    fee: "As per lender policy",
  },
  {
    lender: "IndusInd Bank",
    link:
      "https://www.indusind.com/in/en/personal/loans/personal-loan.html",
    rate: "10.49%*",
    amount: "As per eligibility",
    tenure: "Up to 48 months",
    fee: "As per bank policy",
  },
  {
    lender: "HDFC Bank",
    link: "https://applyonline.hdfcbank.com/personal-loans.html",
    rate: "10.90%*",
    amount: "Up to ₹50 lakh*",
    tenure: "Up to 60 months",
    fee: "As per bank policy",
  },
  {
    lender: "Axis Bank",
    link:
      "https://www.axisbank.com/retail/loans/personal-loan",
    rate: "11.25%*",
    amount: "As per eligibility",
    tenure: "Up to 84 months",
    fee: "As per bank policy",
  },
];

const balanceTransferBenefits = [
  "Potentially lower interest rate",
  "Possible reduction in monthly EMI",
  "More suitable repayment tenure",
  "Option to consolidate eligible obligations",
  "Support with existing lender documents",
  "Assistance throughout transfer processing",
];

const faqItems = [
  {
    question: "What is a personal loan?",
    answer:
      "A personal loan is generally an unsecured loan offered based on income, employment, credit profile and repayment capacity. It may be used for eligible personal requirements.",
  },
  {
    question: "Who can apply for a personal loan?",
    answer:
      "Salaried, self-employed and eligible professional applicants may apply. Final eligibility depends on age, income, work stability, credit score and lender policy.",
  },
  {
    question: "Is collateral required?",
    answer:
      "Personal loans are generally unsecured, so collateral is usually not required. Approval depends primarily on the applicant’s financial and credit profile.",
  },
  {
    question: "How much personal loan can I receive?",
    answer:
      "The eligible amount depends on income, existing EMIs, credit history, employer or business profile and lender assessment.",
  },
  {
    question: "Which documents are generally required?",
    answer:
      "Common documents include PAN, Aadhaar, address proof, salary slips or financial statements, bank statements and employment or business proof.",
  },
  {
    question: "What interest rate will I receive?",
    answer:
      "The final rate depends on the selected lender, applicant profile, credit score, income, loan amount and prevailing lender policy.",
  },
  {
    question: "How long does approval take?",
    answer:
      "Processing time depends on document completeness, credit assessment, employment verification and lender processing.",
  },
  {
    question: "Can I apply online?",
    answer:
      "Yes. You can submit your enquiry online and receive assistance with lender comparison, documents and application processing.",
  },
  {
    question: "Can I transfer an existing personal loan?",
    answer:
      "Eligible borrowers may transfer an outstanding personal loan to another lender for potentially improved terms, subject to assessment and applicable charges.",
  },
  {
    question: "Can I prepay or foreclose the loan?",
    answer:
      "Many lenders permit part-prepayment or foreclosure, subject to lock-in periods, terms and charges stated in the sanction letter.",
  },
];

function PersonalLoan() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      <Seo
        title="Personal Loan in Pune | MONAYRA (OPC) PRIVATE LIMITED"
        description="Explore personal-loan options in Pune with MONAYRA (OPC) PRIVATE LIMITED. Get assistance with eligibility, lender comparison, documentation and loan processing."
        keywords="personal loan Pune, instant personal loan, unsecured loan, personal loan balance transfer, apply personal loan online"
        canonical="https://www.mortbuzz.in/personal-loan"
      />

      <main className="monayra-personal-loan-page">
        <section className="personal-loan-hero">
          <div className="personal-loan-hero-overlay"></div>

          <div className="personal-loan-hero-container">
            <span className="personal-loan-hero-eyebrow animate__animated animate__fadeInDown">
              Unsecured Personal Finance
            </span>

            <h1 className="animate__animated animate__fadeInUp">
              Flexible funds for
              <span> life’s important moments</span>
            </h1>

            <p className="animate__animated animate__fadeInUp animate__delay-1s">
              Compare suitable personal-loan options and receive professional
              assistance for eligibility, documentation, lender processing
              and disbursement.
            </p>

            <div className="personal-loan-hero-actions animate__animated animate__fadeInUp animate__delay-1s">
              <Link
                to="/apply-now"
                className="personal-loan-primary-btn"
              >
                Apply for Personal Loan
                <i className="bi bi-arrow-right"></i>
              </Link>

              <a
                href="#personal-loan-rates"
                className="personal-loan-secondary-btn"
              >
                <i className="bi bi-bar-chart"></i>
                View Indicative Rates
              </a>
            </div>

            <div className="personal-loan-hero-features">
              <div>
                <i className="bi bi-shield-check"></i>
                Generally unsecured
              </div>

              <div>
                <i className="bi bi-bank"></i>
                Multiple lender options
              </div>

              <div>
                <i className="bi bi-file-earmark-check"></i>
                Documentation assistance
              </div>
            </div>
          </div>
        </section>

        <section className="personal-loan-intro-section">
          <div className="personal-loan-page-container personal-loan-intro-grid">
            <div className="personal-loan-intro-image">
              <img
                src="/images/FrontPage/personal-loan-fina-pc-view.png"
                alt="Personal-loan financial assistance"
              />

              <div className="personal-loan-image-overlay"></div>

              <div className="personal-loan-rate-card">
                <span>Indicative starting rate</span>
                <strong>9.99%*</strong>
                <small>Subject to lender assessment</small>
              </div>
            </div>

            <div className="personal-loan-intro-content">
              <span className="personal-loan-section-eyebrow">
                Why Choose MONAYRA?
              </span>

              <h2>
                Personal-loan support designed around
                <span> your financial needs</span>
              </h2>

              <p>
                MONAYRA (OPC) PRIVATE LIMITED helps eligible customers
                compare suitable personal-loan options from selected banks
                and financial institutions.
              </p>

              <p>
                Our team assists with eligibility review, document
                preparation, lender coordination, application tracking and
                disbursement support.
              </p>

              <div className="personal-loan-benefits">
                {[
                  "Indicative rates starting from 9.99%*",
                  "Generally no collateral required",
                  "Options for salaried and self-employed applicants",
                  "Flexible repayment tenure",
                  "Multiple lender comparison",
                  "Support until disbursement",
                ].map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>

                    {item}
                  </div>
                ))}
              </div>

              <Link
                to="/apply-now"
                className="personal-loan-inline-btn"
              >
                Check Your Eligibility
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="personal-loan-uses-section">
          <div className="personal-loan-page-container">
            <div className="personal-loan-section-header">
              <span className="personal-loan-section-eyebrow">
                Flexible Fund Usage
              </span>

              <h2>
                Common uses of a
                <span> personal loan</span>
              </h2>

              <p>
                Personal finance may support eligible planned and unexpected
                expenses, subject to lender terms.
              </p>
            </div>

            <div className="personal-loan-uses-grid">
              {loanUses.map((item) => (
                <article
                  className="personal-loan-use-card"
                  key={item.title}
                >
                  <span className="personal-loan-use-icon">
                    <i className={item.icon}></i>
                  </span>

                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="personal-loan-eligibility-section">
          <div className="personal-loan-page-container">
            <div className="personal-loan-section-header">
              <span className="personal-loan-section-eyebrow">
                Basic Eligibility
              </span>

              <h2>
                Personal-loan
                <span> eligibility factors</span>
              </h2>

              <p>
                Lenders assess income, employment stability, credit history
                and repayment capacity.
              </p>
            </div>

            <div className="personal-loan-eligibility-grid">
              {eligibilityItems.map((item) => (
                <article
                  className="personal-loan-eligibility-card"
                  key={item.title}
                >
                  <div className="personal-loan-eligibility-image">
                    <img
                      src={item.image}
                      alt={item.alt}
                      loading="lazy"
                    />
                  </div>

                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>

            <div className="personal-loan-note">
              <i className="bi bi-info-circle"></i>

              <span>
                Final eligibility depends on lender policy and complete
                assessment of the applicant.
              </span>
            </div>
          </div>
        </section>

        <section className="personal-loan-cta-section">
          <div className="personal-loan-page-container">
            <div className="personal-loan-cta-card">
              <div>
                <span>Start Your Application</span>

                <h2>Need funds for an important requirement?</h2>

                <p>
                  Share your income and required loan amount to explore
                  suitable personal-loan options.
                </p>
              </div>

              <Link
                to="/apply-now"
                className="personal-loan-primary-btn"
              >
                Apply Now
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="personal-loan-documents-section">
          <div className="personal-loan-page-container">
            <div className="personal-loan-section-header">
              <span className="personal-loan-section-eyebrow">
                Document Checklist
              </span>

              <h2>
                Documents generally required for a
                <span> personal loan</span>
              </h2>

              <p>
                The final document list depends on employment type,
                applicant profile and lender requirements.
              </p>
            </div>

            <div className="personal-loan-documents-grid">
              {documentGroups.map((group) => (
                <article
                  className="personal-loan-document-card"
                  key={group.title}
                >
                  <div className="personal-loan-document-heading">
                    <span>
                      <img
                        src={group.image}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                      />
                    </span>

                    <h3>{group.title}</h3>
                  </div>

                  <ul>
                    {group.items.map((item) => (
                      <li key={item}>
                        <i className="bi bi-check-circle"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="personal-loan-process-section">
          <div className="personal-loan-page-container">
            <div className="personal-loan-section-header">
              <span className="personal-loan-section-eyebrow">
                Application Journey
              </span>

              <h2>
                A simple personal-loan
                <span> application process</span>
              </h2>
            </div>

            <div className="personal-loan-process-grid">
              {applicationSteps.map((step, index) => (
                <article
                  className="personal-loan-process-card"
                  key={step.title}
                >
                  <span className="personal-loan-process-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="personal-loan-rates"
          className="personal-loan-rates-section"
        >
          <div className="personal-loan-page-container">
            <div className="personal-loan-section-header">
              <span className="personal-loan-section-eyebrow">
                Indicative Lender Comparison
              </span>

              <h2>
                Personal-loan
                <span> interest-rate overview</span>
              </h2>

              <p>
                Rates, loan limits, tenure and fees are indicative and must
                be confirmed with the selected lender.
              </p>
            </div>

            <div className="personal-loan-table-wrapper">
              <table className="personal-loan-rate-table">
                <thead>
                  <tr>
                    <th>Lender</th>
                    <th>Starting Rate</th>
                    <th>Indicative Amount</th>
                    <th>Maximum Tenure</th>
                    <th>Processing Fee</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {lenderRates.map((lender) => (
                    <tr key={lender.lender}>
                      <td>
                        <a
                          href={lender.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {lender.lender}
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      </td>

                      <td>
                        <strong>{lender.rate}</strong>
                      </td>

                      <td>{lender.amount}</td>
                      <td>{lender.tenure}</td>
                      <td>{lender.fee}</td>

                      <td>
                        <Link
                          to="/apply-now"
                          className="personal-loan-table-btn"
                        >
                          Apply
                          <i className="bi bi-arrow-right"></i>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="personal-loan-note">
              <i className="bi bi-info-circle"></i>

              <span>
                Final terms depend on applicant profile, credit score,
                employment, income and lender policy.
              </span>
            </div>
          </div>
        </section>

        <section className="personal-loan-fee-section">
          <div className="personal-loan-page-container personal-loan-fee-grid">
            <div className="personal-loan-fee-image">
              <img
                src="/images/FrontPage/mortbuzz-home-Loan-10.jpeg"
                alt="Personal-loan processing discussion"
                loading="lazy"
              />
            </div>

            <div className="personal-loan-fee-content">
              <span className="personal-loan-section-eyebrow">
                Loan Charges
              </span>

              <h2>
                Understand the
                <span> processing fee</span>
              </h2>

              <p>
                Lenders may charge a one-time processing fee for application
                assessment, credit checks, verification and loan
                administration.
              </p>

              <p>
                The amount depends on the lender, loan amount, applicant
                profile and available offer. Applicable taxes may also be
                charged.
              </p>

              <div className="personal-loan-fee-highlight">
                <i className="bi bi-receipt"></i>

                <div>
                  <strong>Compare the complete borrowing cost</strong>

                  <span>
                    Review the interest rate, processing fee, insurance,
                    foreclosure and other applicable charges.
                  </span>
                </div>
              </div>

              <Link
                to="/apply-now"
                className="personal-loan-inline-btn"
              >
                Get Suitable Options
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="personal-loan-transfer-section">
          <div className="personal-loan-page-container personal-loan-transfer-grid">
            <div className="personal-loan-transfer-content">
              <span className="personal-loan-section-eyebrow">
                Existing Loan Transfer
              </span>

              <h2>
                Explore a personal-loan
                <span> balance transfer</span>
              </h2>

              <p>
                A balance transfer allows an eligible borrower to move an
                outstanding personal loan to another lender offering
                potentially better repayment terms.
              </p>

              <div className="personal-loan-benefits">
                {balanceTransferBenefits.map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>

                    {item}
                  </div>
                ))}
              </div>

              <Link
                to="/apply-now"
                className="personal-loan-inline-btn"
              >
                Check Transfer Eligibility
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>

            <div className="personal-loan-transfer-image">
              <img
                src="/images/FrontPage/a-professional-portrait-photograph-of-a-_duQebNZKQ82xyx3-MhEhMw_ZEIIy3r6QK2G0OLBe2vLqw.jpeg"
                alt="Personal-loan balance-transfer assistance"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className="personal-loan-faq-section">
          <div className="personal-loan-page-container">
            <div className="personal-loan-section-header">
              <span className="personal-loan-section-eyebrow">
                Common Questions
              </span>

              <h2>
                Personal-loan
                <span> frequently asked questions</span>
              </h2>
            </div>

            <div className="personal-loan-faq-layout">
              <div className="personal-loan-faq-list">
                {faqItems.map((faq, index) => {
                  const isOpen = openFaq === index;

                  return (
                    <article
                      className={`personal-loan-faq-item ${
                        isOpen ? "open" : ""
                      }`}
                      key={faq.question}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenFaq(isOpen ? null : index)
                        }
                        aria-expanded={isOpen}
                      >
                        <span className="personal-loan-faq-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span>{faq.question}</span>

                        <i
                          className={
                            isOpen
                              ? "bi bi-dash-lg"
                              : "bi bi-plus-lg"
                          }
                        ></i>
                      </button>

                      {isOpen && (
                        <div className="personal-loan-faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <aside className="personal-loan-help-card">
                <span className="personal-loan-help-icon">
                  <i className="bi bi-headset"></i>
                </span>

                <span className="personal-loan-help-eyebrow">
                  Need Assistance?
                </span>

                <h3>Speak with our personal-loan team</h3>

                <p>
                  Share your income, employment profile, required loan amount
                  and existing obligations to understand suitable next steps.
                </p>

                <Link
                  to="/contact"
                  className="personal-loan-primary-btn"
                >
                  Contact Our Team
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <Link
                  to="/apply-now"
                  className="personal-loan-help-secondary"
                >
                  <i className="bi bi-file-earmark-text"></i>
                  Apply Online
                </Link>
              </aside>
            </div>
          </div>
        </section>

        <section className="personal-loan-disclaimer">
          <div className="personal-loan-page-container">
            <i className="bi bi-shield-exclamation"></i>

            <p>
              *Displayed rates, loan amounts and product information are
              indicative. Final approval, interest rate, tenure, fees and
              other terms are determined by the selected lender after
              complete assessment. MONAYRA (OPC) PRIVATE LIMITED provides
              loan assistance and does not guarantee approval.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}

export default PersonalLoan;